import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Calendar, FolderKanban, CheckSquare } from "lucide-react";

import { cn } from "@/lib/utils";
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1
import HabitosTab from "@/components/rotina/HabitosTab";
import AgendaTab from "@/components/rotina/AgendaTab";
import ProjetosTab from "@/components/rotina/ProjetosTab";

// Sessão UI-2 — RotinaSection
//
// Section unificada que substitui (no Sidebar) Lembretes + Calendário +
// Hábitos + Projetos. Consiste em hero + 3 tabs internas. Sub-routing
// via hash de 2 níveis (#rotina/habitos|agenda|projetos). Default: habitos.
//
// Componentes legacy permanecem no repo (back-compat) — só some do
// Sidebar. Hash redirect (#habits|#calendar|#reminders|#projects)
// é tratado no NectarDashboard.

const TABS = [
  { id: "habitos",  label: "Hábitos",  icon: Target,        component: HabitosTab },
  { id: "agenda",   label: "Agenda",   icon: Calendar,      component: AgendaTab },
  { id: "projetos", label: "Projetos", icon: FolderKanban,  component: ProjetosTab },
];

function parseHashTab(hash) {
  // Aceita #rotina/habitos, #rotina/agenda, #rotina/projetos.
  // Tudo mais cai no default.
  if (!hash) return null;
  const m = hash.replace(/^#/, "").match(/^rotina\/(habitos|agenda|projetos)$/);
  return m ? m[1] : null;
}

export default function RotinaSection() {
  const [activeTab, setActiveTab] = useState(() => parseHashTab(window.location.hash) || "habitos");

  // Sincroniza hash → tab quando user volta (back/forward)
  useEffect(() => {
    const onHashChange = () => {
      const t = parseHashTab(window.location.hash);
      if (t && t !== activeTab) setActiveTab(t);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [activeTab]);

  // Garante hash sincronizado ao mount (normaliza quando entra direto em #rotina sem subpath)
  useEffect(() => {
    const current = window.location.hash.replace(/^#/, "");
    const expected = `rotina/${activeTab}`;
    if (current !== expected) {
      // replaceState pra não poluir history com cada switch
      window.history.replaceState(null, "", `#${expected}`);
    }
  }, [activeTab]);

  const switchTab = useCallback((id) => {
    setActiveTab(id);
    window.history.replaceState(null, "", `#rotina/${id}`);
  }, []);

  // Keyboard nav: arrow left/right entre tabs
  const handleTabKey = useCallback((e, idx) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const dir = e.key === "ArrowRight" ? 1 : -1;
      const next = TABS[(idx + dir + TABS.length) % TABS.length];
      switchTab(next.id);
    }
  }, [switchTab]);

  const ActiveComponent = TABS.find(t => t.id === activeTab)?.component || HabitosTab;

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="rotina-section">
      {/* UI-1.5 — Ambient layers atrás do conteúdo */}
      <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "var(--ambient-bg-radial)" }}
        aria-hidden="true"
      />
      <div
        className="absolute pointer-events-none"
        style={{
          top: 0, left: 0,
          width: "60%", height: "55%",
          background: "radial-gradient(circle at 18% 25%, rgba(0,255,163,0.08), transparent 50%)",
        }}
        aria-hidden="true"
      />

      <div className="relative px-4 md:px-8 py-6 md:py-8 max-w-[1280px] mx-auto w-full pb-32">
      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative overflow-hidden mb-6 md:mb-8 rounded-2xl glass-card-strong gradient-border-cyan glow-hover"
        data-testid="rotina-hero"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 12% 50%, rgba(0,255,163,0.10), transparent 55%)," +
              "radial-gradient(circle at 90% 0%, rgba(0,212,255,0.08), transparent 50%)",
          }}
        />
        <div className="relative px-6 md:px-10 py-8 md:py-10 flex items-center gap-5 md:gap-7">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-brand-green/15 border border-brand-green/30 flex items-center justify-center shadow-glow-green shrink-0">
            <CheckSquare className="w-7 h-7 md:w-8 md:h-8 text-brand-green" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className="font-display font-bold text-foreground text-[36px] md:text-[48px] lg:text-[56px] leading-[1.05] tracking-[-0.02em]"
              data-testid="rotina-title"
            >
              ROTINA
            </h1>
            <p className="text-body text-foreground-muted mt-1.5 max-w-2xl">
              Hábitos que constroem disciplina, agenda que organiza, projetos que avançam.
            </p>
          </div>
          {/* FaseC C1: sino inline no canto direito do hero. */}
          <div className="shrink-0">
            <NotificationBell />
          </div>
        </div>
      </motion.section>

      {/* Tabs */}
      <motion.div
        role="tablist"
        aria-label="Tabs da rotina"
        className="flex flex-wrap gap-2 mb-6 md:mb-8"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.08, ease: [0.2, 0.8, 0.2, 1] }}
        data-testid="rotina-tabs"
      >
        {TABS.map((tab, idx) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`rotina-tabpanel-${tab.id}`}
              id={`rotina-tab-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => switchTab(tab.id)}
              onKeyDown={(e) => handleTabKey(e, idx)}
              className={cn(
                "inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-medium transition-all duration-fast ease-smooth border",
                isActive
                  ? "bg-brand-cyan/15 border-brand-cyan/40 text-brand-cyan shadow-glow-cyan"
                  : "bg-transparent border-subtle text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
              )}
              data-testid={`rotina-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              {tab.label}
            </button>
          );
        })}
      </motion.div>

      {/* Tab panel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          role="tabpanel"
          id={`rotina-tabpanel-${activeTab}`}
          aria-labelledby={`rotina-tab-${activeTab}`}
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -8 }}
          transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          data-testid={`rotina-tabpanel-${activeTab}`}
        >
          <ActiveComponent />
        </motion.div>
      </AnimatePresence>
      </div>
    </div>
  );
}
